import { plainToInstance } from 'class-transformer'

import { CREATE_PEN_NAME } from '@client/collections/PenName/schemas/createPenName'
import { EDIT_PEN_NAME } from '@client/collections/PenName/schemas/editPenName'
import {
  GET_USER_PEN_NAMES,
  GET_USER_PROFILE_PEN_NAMES,
  GET_PEN_NAME_BY_PEN_NAME,
} from '@client/collections/PenName/schemas/getPenName'
import { GET_PEN_NAME_BY_USER_ID } from '@client/collections/PenName/schemas/getPenNameByUserId'
import { GET_PENNAMES_BY_BANNER } from '@client/collections/PenName/schemas/getPennamesByBanner'
import { REMOVE_PEN_NAME } from '@client/collections/PenName/schemas/removePenName'
import { gqlApiInstance } from '@client/init'
import { PenNameByUserResponse } from '@models/penName/PenNameByUserResponse'
import { PenNameFormType } from '@models/penName/PenNameFormType'
import { PenNameOptionType } from '@models/penName/PenNameOptionType'
import { PenNameType } from '@models/penName/PenNameType'

export function usePenNameAction() {
  async function getPenNameOptions(
    userId: number | undefined
  ): Promise<PenNameOptionType[]> {
    const { getUserPenNames } = await gqlApiInstance.request(
      GET_USER_PEN_NAMES,
      {
        userId,
      }
    )

    return plainToInstance(PenNameOptionType, <any[]>getUserPenNames)
  }

  async function getUserProfilePenNameOptions(
    userId: number | undefined
  ): Promise<PenNameOptionType[]> {
    const { getUserProfilePenNames } = await gqlApiInstance.request(
      GET_USER_PROFILE_PEN_NAMES,
      {
        userId,
      }
    )

    return plainToInstance(PenNameOptionType, <any[]>getUserProfilePenNames)
  }

  async function getPenNames(userId: number): Promise<PenNameType[]> {
    const { getUserPenNames } = await gqlApiInstance.request(
      GET_USER_PEN_NAMES,
      {
        userId,
      }
    )

    return plainToInstance(PenNameType, <any[]>getUserPenNames)
  }

  async function getPenNameByUserId({
    id,
    searchText,
    page,
    perpage,
  }: {
    id: number
    searchText: string
    page: number
    perpage: number
  }): Promise<PenNameByUserResponse> {
    const res = await gqlApiInstance.request(GET_PEN_NAME_BY_USER_ID, {
      id,
      searchText,
      page,
      limitPerPage: perpage,
    })

    return plainToInstance(PenNameByUserResponse, res.getPenNameByUserId)
  }

  async function getPenNameByPenName(penName: string): Promise<PenNameType[]> {
    const { getByPenName } = await gqlApiInstance.request(
      GET_PEN_NAME_BY_PEN_NAME,
      {
        getByPenNameInput: {
          penName,
        },
      }
    )

    return plainToInstance(PenNameType, <any[]>getByPenName)
  }

  async function createPenName(form: PenNameFormType): Promise<PenNameType> {
    const res = await gqlApiInstance?.request(CREATE_PEN_NAME, {
      createPenNameInput: {
        firstPenName: form.primaryPenName,
        secondPenName: form.secondaryPenName,
        facebookLink: form.fbUrl,
        twitterLink: form.twUrl,
      },
    })

    return plainToInstance(PenNameType, res.createPenName)
  }

  async function editPenName(form: PenNameFormType): Promise<PenNameType> {
    const { updatePenName } = await gqlApiInstance?.request(EDIT_PEN_NAME, {
      updatePenNameInput: {
        id: form.id,
        firstPenName: form.primaryPenName,
        secondPenName: form.secondaryPenName,
        facebookLink: form.fbUrl,
        twitterLink: form.twUrl,
      },
    })

    return plainToInstance(PenNameType, updatePenName)
  }

  async function removePenName(id: number): Promise<PenNameType> {
    const res = await gqlApiInstance?.request(REMOVE_PEN_NAME, {
      deletePenNameInput: {
        id,
      },
    })

    return plainToInstance(PenNameType, res.removePenName)
  }

  async function getPennamesByBanner(id: number): Promise<PenNameOptionType[]> {
    const res = await gqlApiInstance.request(GET_PENNAMES_BY_BANNER, {
      getPennamesByBannerId: id,
    })

    return plainToInstance(PenNameOptionType, <any[]>res.getPennamesByBanner)
  }

  return {
    getPenNameOptions,
    getUserProfilePenNameOptions,
    getPenNames,
    getPenNameByUserId,
    getPenNameByPenName,
    createPenName,
    editPenName,
    removePenName,
    getPennamesByBanner,
  }
}
